import React, { useState } from 'react';
import { Button, Space, Table, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTaskItems } from '../../tasks/hooks/useTaskItems';
import TaskFormDrawer from '../../tasks/components/TaskFormDrawer';
import TaskDetailDrawer from '../../tasks/components/TaskDetailDrawer';
import {
  TASK_STATUS_LABELS,
  TASK_PRIORITY_LABELS,
  TASK_PRIORITY_COLORS,
  TASK_TYPE_SHORT_LABELS,
  TASK_TYPE_TAG_COLORS,
  TASK_TYPE_FOLLOWUP,
} from '../../tasks/constants';
import {
  ACTIVITIES_PANEL_TITLE,
  ACTIVITIES_PANEL_EMPTY_CONTACT,
  ACTIVITIES_NEW_FOLLOWUP,
} from '../../tasks/copy';
import type { TaskItemListItem, TaskItemStatus, TaskPriority, TaskType } from '../../tasks/types';
import Can from '../../permissions/Can';
import { usePermission } from '../../permissions/PermissionsContext';

function formatDate(s: string | null | undefined): string {
  if (s == null || s === '') return '—';
  try {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString();
  } catch {
    return '—';
  }
}

export interface ContactTasksPanelProps {
  contactId: string;
}

const ContactTasksPanel: React.FC<ContactTasksPanelProps> = ({ contactId }) => {
  const canView = usePermission('tasks.view');
  const [formOpen, setFormOpen] = useState(false);
  const [editTaskId, setEditTaskId] = useState<string | null>(null);
  const [viewTaskId, setViewTaskId] = useState<string | null>(null);

  const { data: paged, isLoading } = useTaskItems(
    { contactId, pageSize: 20 },
    { enabled: canView }
  );

  if (!canView) {
    return null;
  }

  const columns: ColumnsType<TaskItemListItem> = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (title: string, record) => (
        <a onClick={() => setViewTaskId(record.id)}>{title}</a>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'taskType',
      key: 'taskType',
      width: 90,
      render: (taskType: TaskType) => (
        <Tag color={TASK_TYPE_TAG_COLORS[taskType]}>{TASK_TYPE_SHORT_LABELS[taskType]}</Tag>
      ),
    },
    {
      title: 'Lead',
      dataIndex: 'leadTitle',
      key: 'leadTitle',
      width: 140,
      render: (leadTitle: string | null) => leadTitle ?? '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: TaskItemStatus) => TASK_STATUS_LABELS[status],
    },
    {
      title: 'Issue date',
      dataIndex: 'createdAtUtc',
      key: 'createdAtUtc',
      width: 110,
      render: (date: string | null | undefined) => formatDate(date),
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 100,
      render: (date: string | null) => formatDate(date),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 90,
      render: (priority: TaskPriority) => (
        <Tag color={TASK_PRIORITY_COLORS[priority]}>
          {TASK_PRIORITY_LABELS[priority]}
        </Tag>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Can permission="tasks.edit">
          <Button
            type="link"
            size="small"
            onClick={() => {
              setEditTaskId(record.id);
              setFormOpen(true);
            }}
          >
            Edit
          </Button>
        </Can>
      ),
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <strong>{ACTIVITIES_PANEL_TITLE}</strong>
        <Can permission="tasks.create">
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditTaskId(null);
              setFormOpen(true);
            }}
          >
            {ACTIVITIES_NEW_FOLLOWUP}
          </Button>
        </Can>
      </Space>

      <div className="cp-table-scroll-content">
        <Table
          size="small"
          columns={columns}
          dataSource={paged?.items ?? []}
          loading={isLoading}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: ACTIVITIES_PANEL_EMPTY_CONTACT }}
          scroll={{ x: 'max-content' }}
        />
      </div>

      <TaskFormDrawer
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditTaskId(null);
        }}
        onSuccess={() => {
          setFormOpen(false);
          setEditTaskId(null);
        }}
        editTaskId={editTaskId}
        defaultTaskType={TASK_TYPE_FOLLOWUP}
        initialContactId={contactId}
        lockContact
      />

      <TaskDetailDrawer
        open={!!viewTaskId}
        onClose={() => setViewTaskId(null)}
        taskId={viewTaskId}
      />
    </>
  );
};

export default ContactTasksPanel;
